import { useState, useCallback, useRef } from 'react'
import { createPlanogramProductList } from '@/api/planograms'
import { useSSE, type SSEEvent, type ProgressSSEMessage } from './useSSE'
import { getErrorMessage } from '@/utils/getErrorMessage'

export type PlanogramProductListStatus =
  | 'idle'
  | 'creating'
  | 'processing'
  | 'completed'
  | 'error'

interface State {
  status: PlanogramProductListStatus
  progress: number
  productListId: string | null
  eventsUrl: string | null
  error: string | null
}

const initialState: State = {
  status: 'idle',
  progress: 0,
  productListId: null,
  eventsUrl: null,
  error: null,
}

interface PendingResolver {
  resolve: (id: string) => void
  reject: (error: Error) => void
  productListId: string | null
}

export function usePlanogramProductList() {
  const [state, setState] = useState<State>(initialState)
  const resolverRef = useRef<PendingResolver | null>(null)

  const handleSSEMessage = useCallback((event: SSEEvent) => {
    const data = event.data as ProgressSSEMessage
    const progress = data.percent ?? data.progress
    setState(prev => ({
      ...prev,
      status: 'processing',
      progress: progress ?? prev.progress,
    }))
  }, [])

  const handleSSEComplete = useCallback((event: SSEEvent) => {
    const data = event.data as ProgressSSEMessage
    if (event.type === 'complete' || data.status === 'COMPLETED') {
      setState(prev => ({
        ...prev,
        status: 'completed',
        progress: 100,
        eventsUrl: null,
      }))
      const resolver = resolverRef.current
      resolverRef.current = null
      if (resolver?.productListId) {
        resolver.resolve(resolver.productListId)
      } else {
        resolver?.reject(new Error('Product list ID missing on completion'))
      }
    } else if (event.type === 'error' || data.status === 'FAILED') {
      const errorMessage = data.error ?? data.message ?? 'Processing failed'
      setState(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage,
        eventsUrl: null,
      }))
      const resolver = resolverRef.current
      resolverRef.current = null
      resolver?.reject(new Error(errorMessage))
    }
  }, [])

  const handleSSEError = useCallback((error: Error) => {
    setState(prev => ({
      ...prev,
      status: 'error',
      error: error.message,
      eventsUrl: null,
    }))
    const resolver = resolverRef.current
    resolverRef.current = null
    resolver?.reject(error)
  }, [])

  useSSE({
    url: state.eventsUrl,
    onMessage: handleSSEMessage,
    onComplete: handleSSEComplete,
    onError: handleSSEError,
    enabled: !!state.eventsUrl,
  })

  const create = useCallback(
    (params: {
      pogIds: number[]
      skuNumbers?: string[]
      name?: string
    }): Promise<string> => {
      setState({ ...initialState, status: 'creating' })

      return new Promise<string>((resolve, reject) => {
        // Store resolver — SSE callbacks will settle this promise
        resolverRef.current = { resolve, reject, productListId: null }

        createPlanogramProductList({
          pog_ids: params.pogIds,
          sku_numbers:
            params.skuNumbers && params.skuNumbers.length > 0
              ? params.skuNumbers
              : undefined,
          name: params.name,
        })
          .then(result => {
            // Store id so SSE complete handler can resolve with it
            if (resolverRef.current) {
              resolverRef.current.productListId = result.resource_id
            }
            setState(prev => ({
              ...prev,
              status: 'processing',
              productListId: result.resource_id,
              eventsUrl: result.events_url,
              progress: 0,
            }))
            // Promise stays pending until SSE fires COMPLETED/FAILED
          })
          .catch(error => {
            resolverRef.current = null
            const errorMessage = getErrorMessage(
              error,
              'Failed to create product list'
            )
            setState(prev => ({
              ...prev,
              status: 'error',
              error: errorMessage,
            }))
            reject(error instanceof Error ? error : new Error(errorMessage))
          })
      })
    },
    []
  )

  const reset = useCallback(() => {
    resolverRef.current = null
    setState(initialState)
  }, [])

  return {
    ...state,
    create,
    reset,
    isPending: state.status === 'creating' || state.status === 'processing',
    isCompleted: state.status === 'completed',
    isError: state.status === 'error',
  }
}
